import { type FC, useCallback } from 'react';
import { Icon, IconType } from '../../Props/Icon/Icon';
import styled from 'styled-components';
import { NamedContentEntryText } from '../../ContentEntryText/NamedContentEntryText';

type PaginationControlsProps = {
  pages: number;
  currentPage: number;
  onChange: (v: number) => void;
};

export const PaginationControls: FC<PaginationControlsProps> = ({
  pages,
  currentPage,
  onChange,
}: PaginationControlsProps) => {
  const handleBack = useCallback(() => {
    if (currentPage > 1) {
      onChange(currentPage - 1);
    }
  }, [currentPage, onChange]);

  const handleForward = useCallback(() => {
    if (currentPage < pages) {
      onChange(currentPage + 1);
    }
  }, [currentPage, onChange, pages]);

  return (
    <PaginationControlsContainer>
      <NamedContentEntryText refKey="table" container="span" subKey="page" />
      <NavButton onClick={handleBack} $disabled={currentPage <= 1}>
        <Icon className="rotate" width={20} height={20} icon={IconType.CHEVRON_RIGHT} fill={'transparent'} />
      </NavButton>
      <CurrentPageIndicator>{currentPage}</CurrentPageIndicator>
      <TotalPagesIndicator>
        <NamedContentEntryText refKey="table" container="span" subKey="of" /> {pages}
      </TotalPagesIndicator>
      <NavButton onClick={handleForward} $disabled={currentPage >= pages}>
        <Icon width={20} height={20} icon={IconType.CHEVRON_RIGHT} fill={'transparent'} />
      </NavButton>
    </PaginationControlsContainer>
  );
};

const PaginationControlsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.colors.prototype.mutedForeground};
`;

const NavButton = styled.div<{ $disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 8px;
  height: 8px;
  cursor: pointer;
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
  opacity: ${({ $disabled }) => ($disabled ? 0.3 : 1)};
  color: ${({ theme }) => theme.colors.prototype.mutedForeground};
  transition: color 200ms ease;
  svg path {
    stroke: currentColor;
  }
  &:hover {
    color: ${({ theme }) => theme.colors.prototype.foreground};
  }
  .rotate {
    transform: rotate(180deg);
  }
`;

const CurrentPageIndicator = styled.span`
  color: ${({ theme }) => theme.colors.prototype.foreground};
  font-weight: 500;
`;

const TotalPagesIndicator = styled.span`
  color: ${({ theme }) => theme.colors.prototype.mutedForeground};
`;
